/* eslint-disable max-len */
import { Close } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import * as User from "providers/user";
import { useSidebarState, useTranslate } from "react-admin";
import { useNavigate } from "react-router-dom";
import { CustomButton } from "./CustomButton";
import { driver, DriveStep } from "driver.js";
import "driver.js/dist/driver.css";
import { useEffect } from "react";

const OnboardingModal = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useSidebarState();
  const translate = useTranslate();
  const { permissions, showOnboarding, setShowOnboarding } = User.useUser();

  useEffect(() => {
    const handleStartGuide = (evt: Event) => {
      const custom = evt as CustomEvent<{ guide?: string }>;
      const guide = custom?.detail?.guide || "";
      // if (guide !== "full-guide" && guide !== "stripe-guide") return;

      const isMobile =
        typeof window !== "undefined" &&
        window.matchMedia &&
        window.matchMedia("(max-width: 640px)").matches;

      if (isMobile) {
        setOpen(false);
      }
      if (!isMobile && !open && guide === "full-guide") {
        setOpen(true);
      }

      let driverObj: any = null;

      const steps: DriveStep[] = [
        ...(isMobile
          ? ([
              {
                guideFilterId: "nav-guide",
                element: '[guide-id="burger-menu-button"]',
                popover: {
                  title: "Navigation",
                  description: "Access the menu navigation.",
                  side: "bottom",
                  align: "center",
                  showButtons: ["close", "previous", "next"],
                  onNextClick: () => {
                    setOpen(true);
                    driverObj.moveNext();
                  }
                }
              },
              {
                guideFilterId: "nav-guide",
                element: '[guide-id="menu-nav-items"]',
                popover: {
                  title: "Navigation",
                  description:
                    "Use this menu to navigate to different sections of the admin panel. For this guide, we will automatically redirect you to the necessary sections.",
                  side: "bottom",
                  align: "center",
                  showButtons: ["close", "previous", "next"],
                  onNextClick: () => {
                    setOpen(false);
                    navigate(`/dashboard/Company/${permissions?.companyId}`);
                    driverObj?.moveNext();
                  },
                  onPrevClick: () => {
                    setOpen(false);
                    driverObj?.movePrevious();
                  }
                }
              }
            ] as any)
          : [
              {
                guideFilterId: "nav-guide",
                element: '[guide-id="menu-nav-items"]',
                popover: {
                  title: "Navigation",
                  description:
                    "Use this menu to navigate to different sections of the admin panel. For this guide, we will automatically redirect you to the necessary sections.",
                  side: "right",
                  align: "center",
                  showButtons: ["close", "previous", "next"],
                  onNextClick: () => {
                    navigate(`/dashboard/Company/${permissions?.companyId}`);
                    driverObj.moveNext();
                  }
                }
              }
            ]),
        {
          guideFilterId: "company-guide",
          element: undefined,
          // disableActiveInteraction: true,
          popover: {
            title: "Complete Company Setup",
            description:
              "Fill in all the necessary details to complete your company profile.",
            side: "top",
            showButtons: ["close", "previous", "next"],
            onPrevClick: () => {
              if (isMobile) {
                setOpen(true);
              }
              driverObj.movePrevious();
            }
          }
        },
        {
          guideFilterId: "company-guide",
          element: '[aria-label="Save"]',
          popover: {
            title: "Complete Company Setup",
            description:
              "Don't forget to save your company details before proceeding. Then you will be redirected to company details page.",
            side: "right",
            align: "end",
            showButtons: ["close", "previous", "next"],
            onNextClick: () => {
              if (guide === "company-guide") {
                driverObj.destroy();
                return;
              }
              if (permissions?.isCompanyBasic) {
                navigate("/dashboard/Location/create");
              } else {
                navigate(`/dashboard/Company/${permissions?.companyId}/show`);
              }
              driverObj.moveNext();
            }
          }
        },
        ...(!permissions?.isCompanyBasic
          ? [
              {
                guideFilterId: "stripe-guide",
                element: '[guide-id="edit-company-button"]',
                popover: {
                  title: "Create Stripe Account",
                  description:
                    "You need to go to company edit and follow the steps to create your Stripe account.",
                  side: "right",
                  showButtons: ["close", "previous", "next"],
                  onNextClick: () => {
                    navigate(`/dashboard/Company/${permissions?.companyId}`);
                    driverObj.moveNext();
                  },
                  onPrevClick: () => {
                    navigate(`/dashboard/Company/${permissions?.companyId}`);
                    driverObj.movePrevious();
                  }
                }
              } as any
            ]
          : []),
        ...(!permissions?.isCompanyBasic
          ? [
              {
                guideFilterId: "stripe-guide",
                element: '[guide-id="connect-stripe-button"]',
                popover: {
                  title: "Create Stripe Account",
                  description:
                    "You will be redirected to the Stripe website to complete the account setup, and after finishing, you will be redirected back here.",
                  side: "top",
                  showButtons: ["close", "previous", "next"],
                  onNextClick: () => {
                    if (guide === "stripe-guide") {
                      driverObj.destroy();
                      return;
                    }
                    navigate("/dashboard/Location/create");
                    driverObj.moveNext();
                  },
                  onPrevClick: () => {
                    navigate(
                      `/dashboard/Company/${permissions?.companyId}/show`
                    );
                    driverObj.movePrevious();
                  }
                }
              } as any
            ]
          : []),
        {
          guideFilterId: "location-guide",
          element: undefined,
          popover: {
            title: "Add Location Details",
            description: "Provide the necessary details for the location.",
            side: "top",
            showButtons: ["close", "previous", "next"],
            onPrevClick: () => {
              navigate(`/dashboard/Company/${permissions?.companyId}`);
              driverObj.movePrevious();
            }
          }
        },
        {
          guideFilterId: "location-guide",
          element: '[aria-label="Save"]',
          popover: {
            title: "Add Location Details",
            description:
              "Don't forget to save your location details before proceeding.",
            side: "right",
            align: "end",
            showButtons: ["close", "previous", "next"],
            onNextClick: () => {
              if (guide === "location-guide") {
                driverObj.destroy();
                return;
              }
              navigate("/dashboard/Service");
              setTimeout(() => {
                driverObj.moveNext();
              }, 200);
            }
          }
        },
        {
          guideFilterId: "service-guide",
          element: '[guide-id="service-list-table"]',
          popover: {
            title: "Review and Create Services and Add-ons",
            description: "Review already created services and add-ons",
            side: "top",
            align: "center",
            showButtons: ["close", "previous", "next"],
            onPrevClick: () => {
              navigate("/dashboard/Location/create");
              driverObj.movePrevious();
            }
          }
        },
        {
          guideFilterId: "service-guide",
          element: '[aria-label="Add a service"]',
          popover: {
            title: "Review and Create Services and Add-ons",
            description: "or create new ones",
            side: "left",
            align: "end",
            showButtons: ["close", "previous", "next"],
            onNextClick: () => {
              if (guide === "service-guide") {
                driverObj.destroy();
                return;
              }
              navigate("/dashboard/Menu");
              setTimeout(() => {
                driverObj.moveNext();
              }, 200);
            }
          }
        },
        {
          guideFilterId: "menu-guide",
          element: '[guide-id="menu-list-table"]',
          popover: {
            title: "Compile Menu",
            description:
              "You can customize the default menu that is already set up and assign it to your location.",
            side: "bottom",
            align: "center",
            showButtons: ["close", "previous", "next"],
            onPrevClick: () => {
              navigate("/dashboard/Service");
              driverObj.movePrevious();
            }
          }
        },
        {
          guideFilterId: "menu-guide",
          element: '[aria-label="Add a menu"]',
          popover: {
            title: "Compile Menu",
            description: "or create new one.",
            side: "left",
            align: "end",
            showButtons: ["close", "previous", "next"],
            onNextClick: () => {
              if (guide === "menu-guide") {
                driverObj.destroy();
                return;
              }
              navigate("/dashboard/Artist");
              driverObj.moveNext();
            }
          }
        },
        {
          guideFilterId: "artist-guide",
          element: '[aria-label="Add a member"]',
          popover: {
            title: "Add Artists",
            description: "Create artists first, then set their availabilities.",
            side: "left",
            align: "end",
            showButtons: ["close", "previous", "next"],
            onNextClick: () => {
              navigate("/dashboard/Artist/create");
              driverObj.moveNext();
            },
            onPrevClick: () => {
              navigate("/dashboard/Menu");
              driverObj.movePrevious();
            }
          }
        },
        {
          guideFilterId: "artist-guide",
          element: '[aria-label="Save"]',
          popover: {
            title: "Add Artists",
            description:
              "Fill out the artist information and remember to save before moving on.",
            side: "right",
            align: "end",
            showButtons: ["close", "previous", "next"],
            onNextClick: () => {
              if (guide === "artist-guide") {
                driverObj.destroy();
                return;
              }
              if (permissions?.isCompanyBasic) {
                navigate("/dashboard/");
              } else {
                navigate("/dashboard/Availability");
              }
              setTimeout(() => {
                driverObj.moveNext();
              }, 1500);
            },
            onPrevClick: () => {
              navigate("/dashboard/Artist");
              driverObj.movePrevious();
            }
          }
        },
        ...(!permissions?.isCompanyBasic
          ? [
              {
                guideFilterId: "availability-guide",
                element: '[guide-id="availability-date-and-location-filters"]',
                popover: {
                  title: "Set Artist Availability",
                  description:
                    "You can filter the availability calendar by date and location.",
                  side: isMobile ? "right" : "bottom",
                  align: isMobile ? "start" : "center",
                  showButtons: ["close", "previous", "next"],
                  onPrevClick: () => {
                    navigate("/dashboard/Artist/create");
                    driverObj.movePrevious();
                  }
                }
              } as any
            ]
          : []),
        ...(!permissions?.isCompanyBasic
          ? [
              {
                guideFilterId: "availability-guide",
                element: '[guide-id="add-availability-button"]',
                popover: {
                  title: "Set Artist Availability",
                  description:
                    "Set up availabilities for your artists so clients can book appointments. You can also click on calendar slots to select a start time plus 15 minutes. To choose a later end time, drag the mouse or finger to the desired time (on mobile devices, long press and then drag).",
                  side: isMobile ? "right" : "bottom",
                  align: isMobile ? "start" : "center",
                  showButtons: ["close", "previous", "next"],
                  onNextClick: () => {
                    if (guide === "availability-guide") {
                      driverObj.destroy();
                      return;
                    }
                    driverObj.moveNext();
                  }
                }
              } as any
            ]
          : []),
        {
          guideFilterId: "finish-guide",
          element: '[guide-id="profile-menu-item"]',
          popover: {
            title: "Tour Finished",
            description:
              "You have finished the onboarding tour. You can revisit this tour anytime from the profile section.",
            side: "right",
            align: "end",
            showButtons: ["close", "previous", "next"],
            onNextClick: () => {
              driverObj.destroy();
              localStorage.setItem("onboardingGuideFinished", "true");
              if (guide === "full-guide") {
                setTimeout(() => {
                  setShowOnboarding(false);
                }, 200);
              }
            },
            onPrevClick: () => {
              if (permissions?.isCompanyBasic) {
                navigate("/dashboard/Artist/create");
              }
              driverObj.movePrevious();
            }
          }
        }
      ];

      driverObj = driver({
        showProgress: true,
        allowClose: false,
        onDestroyed: () => {
          setShowOnboarding(true);
        },
        disableActiveInteraction: true,
        steps: steps.filter((step: any) => {
          return guide === "full-guide" ? true : guide === step.guideFilterId;
        })
        // steps: ?.filter((step) => {
        //   return guide === "full-guide" ? true : guide === step.guideFilterId;
        // })
      });

      setTimeout(() => driverObj.drive(), 500);
    };

    window.addEventListener(
      "start-onboarding-guide" as any,
      handleStartGuide as EventListener
    );
    return () => {
      window.removeEventListener(
        "start-onboarding-guide" as any,
        handleStartGuide as EventListener
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, translate]);

  return (
    <div>
      {showOnboarding && (
        <div className="w-[95%] max-w-[545px] fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 rounded-lg bg-[#fff] shadow-2xl drop-shadow-2xl border border-[rgba(48,47,46,0.23)] p-4">
          <div className="flex justify-between items-start gap-4">
            <div className="text-[16px] font-[500]">
              {
                "Want a quick tour to set up your account? We won't create anything; it's just an informative walkthrough to help you get started. You can turn it off anytime."
              }
            </div>
            <IconButton
              className="!mt-[-8px] !mr-[-8px]"
              onClick={() => {
                localStorage.setItem("onboardingGuideFinished", "true");
                setShowOnboarding(false);
              }}
            >
              <Close />
            </IconButton>
          </div>
          <div className="">
            <div className="flex gap-2 items-center mt-4">
              <CustomButton
                text="Start a Tour"
                onClick={() => {
                  try {
                    window.dispatchEvent(
                      new CustomEvent("start-onboarding-guide", {
                        detail: { guide: "full-guide" }
                      })
                    );
                    setShowOnboarding(false);
                  } catch (e) {}
                }}
              />
            </div>
            <div className="text-[16px] font-[500] my-4">
              or you can view each section separately:
            </div>
            <ul className="list-disc pl-4">
              <li
                className="text-[14px] font-[400] w-fit cursor-pointer hover:underline"
                aria-hidden
                onClick={() => {
                  try {
                    navigate(`/dashboard/Company/${permissions?.companyId}`);
                    setTimeout(() => {
                      window.dispatchEvent(
                        new CustomEvent("start-onboarding-guide", {
                          detail: { guide: "company-guide" }
                        })
                      );
                    }, 200);
                    setShowOnboarding(false);
                  } catch (e) {}
                }}
              >
                {"Complete Company Setup"}
              </li>
              {!permissions?.isCompanyBasic && (
                <li
                  className="text-[14px] font-[400] w-fit cursor-pointer hover:underline"
                  aria-hidden
                  onClick={() => {
                    try {
                      navigate(
                        `/dashboard/Company/${permissions?.companyId}/show`
                      );
                      setTimeout(() => {
                        window.dispatchEvent(
                          new CustomEvent("start-onboarding-guide", {
                            detail: { guide: "stripe-guide" }
                          })
                        );
                      }, 200);
                      setShowOnboarding(false);
                    } catch (e) {}
                  }}
                >
                  {"Create Stripe Account"}
                </li>
              )}
              <li
                className="text-[14px] font-[400] w-fit cursor-pointer hover:underline"
                aria-hidden
                onClick={() => {
                  try {
                    navigate("/dashboard/Location/create");
                    setTimeout(() => {
                      window.dispatchEvent(
                        new CustomEvent("start-onboarding-guide", {
                          detail: { guide: "location-guide" }
                        })
                      );
                    }, 200);
                    setShowOnboarding(false);
                  } catch (e) {}
                }}
              >
                {"Add Location Details"}
              </li>
              <li
                className="text-[14px] font-[400] w-fit cursor-pointer hover:underline"
                aria-hidden
                onClick={() => {
                  try {
                    navigate("/dashboard/Service");
                    setTimeout(() => {
                      window.dispatchEvent(
                        new CustomEvent("start-onboarding-guide", {
                          detail: { guide: "service-guide" }
                        })
                      );
                    }, 200);
                    setShowOnboarding(false);
                  } catch (e) {}
                }}
              >
                {"Review and Create Services and Add-ons"}
              </li>
              <li
                className="text-[14px] font-[400] w-fit cursor-pointer hover:underline"
                aria-hidden
                onClick={() => {
                  try {
                    navigate("/dashboard/Menu");
                    setTimeout(() => {
                      window.dispatchEvent(
                        new CustomEvent("start-onboarding-guide", {
                          detail: { guide: "menu-guide" }
                        })
                      );
                    }, 200);
                    setShowOnboarding(false);
                  } catch (e) {}
                }}
              >
                {"Compile Menu"}
              </li>
              <li
                className="text-[14px] font-[400] w-fit cursor-pointer hover:underline"
                aria-hidden
                onClick={() => {
                  try {
                    navigate("/dashboard/Artist");
                    setTimeout(() => {
                      window.dispatchEvent(
                        new CustomEvent("start-onboarding-guide", {
                          detail: { guide: "artist-guide" }
                        })
                      );
                    }, 200);
                    setShowOnboarding(false);
                  } catch (e) {}
                }}
              >
                {"Add Artists"}
              </li>
              {!permissions?.isCompanyBasic && (
                <li
                  className="text-[14px] font-[400] w-fit cursor-pointer hover:underline"
                  aria-hidden
                  onClick={() => {
                    try {
                      navigate("/dashboard/Availability");
                      setTimeout(() => {
                        window.dispatchEvent(
                          new CustomEvent("start-onboarding-guide", {
                            detail: { guide: "availability-guide" }
                          })
                        );
                      }, 1500);
                      setShowOnboarding(false);
                    } catch (e) {}
                  }}
                >
                  {"Set Artist Availability"}
                </li>
              )}
            </ul>
            {/* <div className="flex gap-2 items-center mt-2">
              <div className="text-[16px] text-[rgba(48,47,46,0.95)]">3.</div>
              <button
                className="h-[32px] px-3 rounded-[16px] border border-[rgba(48,47,46,0.23)] bg-white text-[14px]"
                onClick={() => {
                  setShowOnboarding(false);
                  try {
                    localStorage.setItem("onboardingPopupDismissed", "1");
                  } catch (e) {}
                }}
              >
                {"Dismiss"}
              </button>
            </div> */}
          </div>
        </div>
      )}
    </div>
  );
};

export default OnboardingModal;
